"use client";

import { cn } from "@jesus-central-church/shared";
import { motion, type Variants } from "motion/react";
import { type ReactNode } from "react";

interface ScriptureSectionProperties {
  badge?: string;
  scripture: string;
  reference: string;
  textColor?: string;
  variant?: "" | "default" | "editorial" | "minimal";
  accentColor?: "" | "gold" | "red" | "green";
  className?: string;
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 30 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const quoteMarkVariants: Variants = {
  hidden: { opacity: 0, scale: 0.8 },
  show: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 1,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const lineVariants: Variants = {
  hidden: { scaleX: 0 },
  show: {
    scaleX: 1,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const accentColors = {
  gold: "var(--strategy-gold, #c89657)",
  red: "var(--strategy-red, #eb3700)",
  green: "var(--strategy-green, #746e06)",
} as const;

interface QuoteMarkProps {
  position: "open" | "close";
  color: string;
}

const QuoteMark = ({ position, color }: QuoteMarkProps): ReactNode => (
  <motion.span
    variants={quoteMarkVariants}
    className="pointer-events-none select-none font-accent"
    style={{
      color,
      fontSize: "clamp(4rem, 10vw, 8rem)",
      lineHeight: 1,
      fontStyle: "normal",
      fontWeight: 400,
    }}
    aria-hidden="true"
  >
    {position === "open" ? "\u201C" : "\u201D"}
  </motion.span>
);

export const Scripture = ({
  badge,
  scripture,
  reference,
  textColor,
  variant = "default",
  accentColor = "gold",
  className,
}: ScriptureSectionProperties): ReactNode => {
  const accent = accentColors[accentColor as keyof typeof accentColors];
  const textColorValue = textColor
    ? `var(--${textColor}, var(--freedom))`
    : "var(--freedom, #edf2e9)";

  // Consistent spacing: Scripture is a standalone component
  // When used inside Section, Section handles outer padding
  // When used standalone, it needs its own container

  if (variant === "minimal") {
    return (
      <div
        className={cn("mx-auto max-w-4xl text-center", className)}
        style={{ color: textColorValue }}
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="space-y-6"
        >
          <motion.blockquote
            variants={itemVariants}
            className="font-accent text-2xl leading-relaxed font-normal italic text-balance sm:text-3xl md:text-4xl"
          >
            "{scripture}"
          </motion.blockquote>
          <motion.p
            variants={itemVariants}
            className="font-body text-sm font-medium tracking-[0.2em] uppercase opacity-70"
          >
            — {reference}
          </motion.p>
        </motion.div>
      </div>
    );
  }

  if (variant === "editorial") {
    return (
      <div
        className={cn(
          "relative mx-auto max-w-5xl",
          className
        )}
        style={{ color: textColorValue }}
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="relative"
        >
          {/* Decorative vertical line */}
          <motion.div
            variants={lineVariants}
            className="absolute top-0 left-0 h-full w-[3px] origin-top"
            style={{ backgroundColor: accent }}
          />

          <div className="pl-8 md:pl-12">
            {badge && (
              <motion.span
                variants={itemVariants}
                className="mb-8 inline-block font-heading text-xs tracking-[0.3em] uppercase"
                style={{ color: accent }}
              >
                {badge}
              </motion.span>
            )}

            <motion.blockquote
              variants={itemVariants}
              className="mb-8 font-accent text-3xl leading-snug font-normal italic text-balance sm:text-4xl md:text-5xl lg:text-6xl"
            >
              {scripture}
            </motion.blockquote>

            <motion.footer variants={itemVariants} className="flex items-center gap-4">
              <span
                className="h-[2px] w-12"
                style={{ backgroundColor: accent }}
              />
              <cite className="font-body text-sm font-medium tracking-[0.15em] uppercase not-italic opacity-80">
                {reference}
              </cite>
            </motion.footer>
          </div>
        </motion.div>
      </div>
    );
  }

  // Default variant - centered with decorative quote marks
  return (
    <div
      className={cn(
        "relative mx-auto max-w-5xl overflow-hidden",
        className
      )}
      style={{ color: textColorValue }}
    >
      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.3 }}
        className="relative text-center"
      >
        {/* Top decorative line */}
        <motion.div
          variants={lineVariants}
          className="mx-auto mb-12 h-[2px] w-16 origin-center"
          style={{ backgroundColor: accent }}
        />

        {badge && (
          <motion.span
            variants={itemVariants}
            className="mb-10 inline-block rounded-full px-5 py-2 font-heading text-xs tracking-[0.3em] uppercase"
            style={{
              backgroundColor: `color-mix(in srgb, ${accent} 15%, transparent)`,
              color: accent,
            }}
          >
            {badge}
          </motion.span>
        )}

        {/* Scripture with decorative quote marks */}
        <div className="relative">
          {/* Opening quote mark - positioned above and left */}
          <div className="absolute -top-8 left-0 opacity-20 md:-top-12 md:left-4 lg:left-12">
            <QuoteMark position="open" color={accent} />
          </div>

          <motion.blockquote
            variants={itemVariants}
            className="relative z-10 mx-auto max-w-4xl px-4 font-accent text-2xl leading-relaxed font-normal italic text-balance sm:text-3xl md:px-12 md:text-4xl lg:text-5xl"
            style={{
              fontFeatureSettings: '"liga" 1, "kern" 1',
            }}
          >
            {scripture}
          </motion.blockquote>

          {/* Closing quote mark - positioned below and right */}
          <div className="absolute -bottom-12 right-0 opacity-20 md:-bottom-16 md:right-4 lg:right-12">
            <QuoteMark position="close" color={accent} />
          </div>
        </div>

        {/* Reference with decorative elements */}
        <motion.footer
          variants={itemVariants}
          className="mt-12 flex items-center justify-center gap-4"
        >
          <span
            className="h-[1px] w-8 opacity-40"
            style={{ backgroundColor: textColorValue }}
          />
          <cite
            className="font-body text-sm font-medium tracking-[0.2em] uppercase not-italic"
            style={{ color: accent }}
          >
            {reference}
          </cite>
          <span
            className="h-[1px] w-8 opacity-40"
            style={{ backgroundColor: textColorValue }}
          />
        </motion.footer>

        {/* Bottom decorative line */}
        <motion.div
          variants={lineVariants}
          className="mx-auto mt-12 h-[2px] w-16 origin-center"
          style={{ backgroundColor: accent }}
        />
      </motion.div>
    </div>
  );
};
